import { HOTEL_LIST } from "@elit-hair/elitklinik-shared";
import { Tooltip } from "@mui/material";
import { createElement } from "react";
import { TbArrowRight, TbBuildingSkyscraper } from "react-icons/tb";

import TableListItem from "@/components/table/TableListItem";
import {
    CLINIC_DESTINATION_OPTIONS,
    ClinicDestinationOption,
    DEFAULT_CLINIC_DESTINATION_ICON,
} from "@/constants/clinicDestinationOptions";
import { HOTEL_CLINIC_NAME_LABELS, HotelClinicNames } from "@/models/hotel-clinic.model";

export type ClinicArrivalRouteCellProps = {
    hotel: string;
    clinic: HotelClinicNames;
    isCancelled?: boolean;
};

type RouteIconTooltipProps = {
    label: string;
    children: React.ReactNode;
};

function RouteIconTooltip({ label, children }: RouteIconTooltipProps) {
    return (
        <Tooltip title={label} arrow placement="top">
            <div className="shrink-0 cursor-default" aria-label={label}>
                {children}
            </div>
        </Tooltip>
    );
}

type LocationImageProps = {
    src?: string;
    alt: string;
    Icon?: ClinicDestinationOption["Icon"];
    iconClassName?: string;
    isCancelled?: boolean;
};

function LocationImage({ src, alt, Icon, iconClassName, isCancelled }: LocationImageProps) {
    return (
        <div
            className={
                "relative shrink-0 overflow-hidden rounded-md ring-1 ring-slate-200 shadow-sm h-9 aspect-video " +
                (isCancelled ? "bg-red-50" : "bg-white")
            }
        >
            {src ? (
                <img src={src} alt={alt} className="h-full w-full object-contain p-1" />
            ) : Icon ? (
                <div className="flex h-full w-full items-center justify-center">
                    {createElement(Icon, {
                        className: iconClassName ?? "h-4 w-4 text-slate-500",
                    })}
                </div>
            ) : null}
        </div>
    );
}

function ClinicVisual({ option, isCancelled }: { option: ClinicDestinationOption; isCancelled?: boolean }) {
    return (
        <RouteIconTooltip label={option.label}>
            <LocationImage
                src={option.logo}
                alt={option.label}
                Icon={option.Icon ?? DEFAULT_CLINIC_DESTINATION_ICON}
                iconClassName={option.iconClassName}
                isCancelled={isCancelled}
            />
        </RouteIconTooltip>
    );
}

function ClinicArrivalRouteCell({ hotel, clinic, isCancelled = false }: ClinicArrivalRouteCellProps) {
    const hotelEntry = hotel ? HOTEL_LIST.find((item) => item.name === hotel) : null;
    const clinicOption = CLINIC_DESTINATION_OPTIONS.find((item) => item.value === clinic);
    const clinicLabel = clinicOption?.label ?? HOTEL_CLINIC_NAME_LABELS[clinic] ?? clinic;
    const hotelLabel = hotelEntry?.name ?? "Otel seçilmedi";

    const fallbackBoxClassName =
        "flex h-9 aspect-video items-center justify-center rounded-md ring-1 ring-slate-200 shadow-sm " +
        (isCancelled ? "bg-red-50 text-red-500" : "bg-white text-slate-500");

    return (
        <div className="flex h-full w-full items-center px-1 py-1">
            <TableListItem className={["w-full min-w-0 px-2 py-1"]}>
                <div className="flex w-full min-w-0 items-center justify-between gap-2">
                    <RouteIconTooltip label={hotelLabel}>
                        {hotelEntry ? (
                            <LocationImage src={hotelEntry.logo} alt={hotelLabel} isCancelled={isCancelled} />
                        ) : (
                            <div className={fallbackBoxClassName}>
                                <TbBuildingSkyscraper className="h-5 w-5 shrink-0" />
                            </div>
                        )}
                    </RouteIconTooltip>

                    <TbArrowRight className="mx-1 h-4 w-4 shrink-0 text-slate-400" aria-hidden />

                    {clinicOption ? (
                        <ClinicVisual option={clinicOption} isCancelled={isCancelled} />
                    ) : (
                        <RouteIconTooltip label={clinicLabel}>
                            <div className={fallbackBoxClassName}>
                                <DEFAULT_CLINIC_DESTINATION_ICON className="h-5 w-5 shrink-0" />
                            </div>
                        </RouteIconTooltip>
                    )}
                </div>
            </TableListItem>
        </div>
    );
}

export default ClinicArrivalRouteCell;
